import { AIReview, AIReviewStatus, AIReviewFeedback, FEEDBACK_ITEMS, FeedbackItem, getPromptTypeKoreanName } from "../common/models/types";

// 시간을 분:초 형식으로 변환 (밀리초 입력)
const formatDuration = (milliseconds: number | undefined): string => {
  if (!milliseconds) return "-";
  
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes > 0) {
    return `${minutes}분 ${seconds}초`;
  }
  return `${seconds}초`;
};

// AI Review 상태를 한글로 변환
const getStatusLabel = (status: number): string => {
  switch (status) {
    case -1: // FAIL
      return "실패";
    case AIReviewStatus.PENDING:
      return "대기중";
    case AIReviewStatus.READY:
      return "준비됨";
    case AIReviewStatus.PROCESSING:
    case AIReviewStatus.PROCESSING_USER_INFO:
    case AIReviewStatus.PROCESSING_PRESCRIPTION:
    case AIReviewStatus.PROCESSING_DRUG_CRAWLING:
    case AIReviewStatus.PROCESSING_DRUG_INFO:
    case AIReviewStatus.PROCESSING_PROMPT_CREATION:
    case AIReviewStatus.PROCESSING_LLM_ANALYSIS:
      return "처리중";
    case AIReviewStatus.COMPLETED:
      return "완료";
    default:
      return "알 수 없음";
  }
};

// 보고서 종류 표시
const getReviewType = (review: any): string => {
  if (review.promptType) {
    const { getPromptTypeKoreanName } = require("../common/models/types");
    return getPromptTypeKoreanName(review.promptType);
  }
  return review.promptId || "일반";
};

// CSV 문자열 이스케이프 처리
const escapeCSV = (value: any): string => {
  if (value === null || value === undefined) return "";
  
  const str = String(value);
  // 큰따옴표, 쉼표, 줄바꿈이 포함된 경우 큰따옴표로 감싸고 내부 큰따옴표는 두 개로 변환
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// 포인트 과금 여부 판별 (환불 버튼 조건과 동일)
const getChargeStatus = (review: any): string => {
  // pointsTransactionId가 있으면 포인트가 과금된 것
  if (review.pointsTransactionId) {
    return review.analysis?.refund ? "환불됨" : "유료";
  }
  return "무료";
};

// AI 리뷰 데이터를 CSV 형식으로 변환
export const exportAIReviewsToCSV = (reviews: AIReview[]): string => {
  // CSV 헤더
  const headers = [
    "번호",
    "환자번호",
    "분석종류",
    "면허번호",
    "사용자 이메일",
    "사용자 이름",
    "전화번호",
    "프리미엄",
    "과금상태",
    "상태",
    "환불여부",
    "비용",
    "처리시간",
    "처방전 개수",
    "총 의약품 수",
    "고유 의약품 수",
    "생성일",
    "메모"
  ];

  // CSV 데이터 행들
  const rows = reviews.map(review => {
    const row = [
      review.id,
      review.patientId || "-",
      getReviewType(review),
      review.user?.license || "-",
      review.user?.email || "-",
      review.user?.name || "-",
      review.user?.phone || "-",
      (review as any).premiumRole || "-",
      getChargeStatus(review),
      getStatusLabel(review.status || 0),
      review.analysis?.refund ? "환불됨" : "-",
      review.analysis?.cost ? `$${review.analysis.cost.toLocaleString()}` : "$0",
      formatDuration(review.analysis?.timings?.LIFE?.durationMs),
      review.analysis?.initial?.prescriptionCount || 0,
      review.analysis?.initial?.totalMedicines || 0,
      review.analysis?.initial?.uniqueMedicines || 0,
      review.createdAt ? new Date(review.createdAt).toLocaleDateString("ko-KR") : "-",
      review.memo || "-"
    ];

    return row.map(escapeCSV).join(",");
  });

  // 헤더와 데이터 행들을 합쳐서 CSV 문자열 생성
  return [headers.map(escapeCSV).join(","), ...rows].join("\n");
};

// 현재 날짜를 YYYY-MM-DD 형식으로 반환
export const getFormattedDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 피드백 항목 배열을 한글 문자열로 변환
const formatFeedbackItems = (items: FeedbackItem[] | null): string => {
  if (!items || items.length === 0) return "-";
  return items.map(item => FEEDBACK_ITEMS[item] || item).join(", ");
};

// AI 리뷰 피드백 데이터를 CSV 형식으로 변환
export const exportFeedbacksToCSV = (feedbacks: AIReviewFeedback[]): string => {
  // CSV 헤더
  const headers = [
    "피드백ID",
    "리뷰ID",
    "환자번호",
    "분석종류",
    "사용자 이메일",
    "사용자 이름",
    "별점",
    "좋았던점",
    "아쉬운점",
    "코멘트",
    "피드백일"
  ];

  // CSV 데이터 행들
  const rows = feedbacks.map(feedback => {
    const aiReview = feedback.aiReview;
    const user = feedback.user; // 사용자 정보는 feedback.user에 있음
    const patientId = (aiReview as any)?.request?.patientId || aiReview?.patientId; // patientId는 request 안에 있음
    const row = [
      feedback.id,
      feedback.aiReviewId,
      patientId || "-",
      getPromptTypeKoreanName(aiReview?.promptType),
      user?.email || "-",
      user?.name || "-",
      feedback.rating || 0,
      formatFeedbackItems(feedback.likes),
      formatFeedbackItems(feedback.dislikes),
      feedback.comment || "-",
      feedback.updatedAt ? new Date(feedback.updatedAt).toLocaleDateString("ko-KR") : "-"
    ];

    return row.map(escapeCSV).join(",");
  });

  // 헤더와 데이터 행들을 합쳐서 CSV 문자열 생성
  return [headers.map(escapeCSV).join(","), ...rows].join("\n");
};